<template>
  <div
    v-if="totalResults > 0"
    class="flex flex-col sm:flex-row sm:items-center sm:justify-between mt-4 sm:mt-6 md:mt-8 space-y-3 sm:space-y-0 px-1 sm:px-2"
  >
    <!-- Results summary -->
    <div
      class="text-xs sm:text-sm text-slate-600 dark:text-slate-400 font-medium text-center sm:text-left"
    >
      <template v-if="!isLoading">
        Showing
        <span class="font-bold text-slate-900 dark:text-white">
          {{ (currentPage - 1) * limit + 1 }}
        </span>
        to
        <span class="font-bold text-slate-900 dark:text-white">
          {{ Math.min(currentPage * limit, totalResults) }}
        </span>
        of
        <span class="font-bold text-slate-900 dark:text-white">
          {{ totalResults }}
        </span>
        results
      </template>
      <template v-else>
        <div class="flex items-center gap-2 justify-center sm:justify-start">
          <AppLoader variant="button" class="w-3.5 h-3.5 text-indigo-600" />
          <span class="text-xs text-slate-500 dark:text-slate-400 font-medium">Loading results...</span>
        </div>
      </template>
    </div>

    <!-- Pagination buttons -->
    <div class="flex items-center justify-center gap-1 sm:gap-1.5 flex-wrap">
      <!-- Previous button -->
      <button
        @click="$emit('go-to-page', currentPage - 1)"
        :disabled="currentPage === 1 || isLoading"
        class="px-2 sm:px-3 py-1.5 text-xs sm:text-sm font-semibold text-slate-600 dark:text-slate-300 hover:text-indigo-600 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-[#18243A] rounded-xl transition-all duration-200 disabled:opacity-40 disabled:cursor-not-allowed min-h-[32px] sm:min-h-[32px] min-w-[32px] sm:min-w-[32px] border border-slate-200 dark:border-[#263449] bg-white dark:bg-[#111C30] touch-manipulation flex items-center justify-center"
      >
        <ChevronLeft class="w-4 h-4" />
      </button>

      <!-- Numbered buttons -->
      <template
        v-for="page in Math.min(totalPages, 5)"
        :key="page"
      >
        <button
          @click="$emit('go-to-page', page)"
          :disabled="isLoading"
          :class="[
            'px-2.5 sm:px-3 py-1.5 text-xs sm:text-sm rounded-xl transition-all duration-200 min-h-[32px] sm:min-h-[32px] min-w-[32px] sm:min-w-[32px] border touch-manipulation',
            page === currentPage
              ? 'font-bold bg-indigo-600 text-white border-indigo-600 shadow-xs'
              : 'font-semibold text-slate-600 dark:text-slate-300 hover:text-indigo-600 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-[#18243A] border-slate-200 dark:border-[#263449] bg-white dark:bg-[#111C30]',
            'disabled:opacity-40 disabled:cursor-not-allowed',
          ]"
        >
          {{ page }}
        </button>
      </template>

      <!-- Next button -->
      <button
        @click="$emit('go-to-page', currentPage + 1)"
        :disabled="currentPage === totalPages || isLoading"
        class="px-2 sm:px-3 py-1.5 text-xs sm:text-sm font-semibold text-slate-600 dark:text-slate-300 hover:text-indigo-600 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-[#18243A] rounded-xl transition-all duration-200 disabled:opacity-40 disabled:cursor-not-allowed min-h-[32px] sm:min-h-[32px] min-w-[32px] sm:min-w-[32px] border border-slate-200 dark:border-[#263449] bg-white dark:bg-[#111C30] touch-manipulation flex items-center justify-center"
      >
        <ChevronRight class="w-4 h-4" />
      </button>
    </div>
  </div>
</template>

<script setup>
  import { ChevronLeft, ChevronRight } from "lucide-vue-next";

  defineProps({
    currentPage: {
      type: Number,
      default: 1,
    },
    totalPages: {
      type: Number,
      default: 1,
    },
    totalResults: {
      type: Number,
      default: 0,
    },
    limit: {
      type: Number,
      default: 20,
    },
    isLoading: {
      type: Boolean,
      default: false,
    },
  });

  defineEmits(["go-to-page"]);
</script>

<style scoped>
  .touch-manipulation {
    touch-action: manipulation;
    -webkit-tap-highlight-color: transparent;
  }
</style>
